import { useAccountId } from '@labrav/common'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  BoxV2,
  ButtonV2,
  ColorSelectorV2,
  LabraToolTipV2,
  NoteV2,
} from '@labrav/react-components'
import React, { useEffect, useState } from 'react'
import { isEmpty } from 'lodash'
import { faHashtag } from '@fortawesome/pro-regular-svg-icons'
import { Grid, SelectChangeEvent, Typography } from '@mui/material'
import { useStyles } from './FlyOutProductBranding.styles'
import { FileUploadWithLoader } from '@labrav/common'
import { FormikProps, useFormikContext } from 'formik'
import { DefaultNowType } from '../../../common/utils/dateTimeNow'
import { getValidValueBetweenMinMaxFloat } from '@labrav/common'
import { FormLockedType } from '../../../types/FormLockedType'
import { bucketName } from '../../../common/utils/constants'
import {
  extractColorV2,
  fileFormatIncorrectText,
  hexadecimalColor,
  fileUploadContainerTitle,
  fileUploadButtonText,
  imageFileUploadDescriptionLandingPage,
} from '../../../common/utils/productCreationUtil'
import { convertRGBAAlpha } from '../../../common/utils/convertRGBAAlpha'
import { FlyoutProductSections } from '../../../types/addEditProduct'
import { FlyoutProductBrandingFaqs } from './FlyoutProductBrandingFaqs'
export interface FlyOutProductBrandingProps {
  defaultNow: DefaultNowType
  detailView?: boolean
  detailViewTooltip?: string
  handleRefLinkClick?: (inex: number) => void
  productView?: boolean
}

export const FlyOutProductBranding: React.FC<
  FlyOutProductBrandingProps & FormLockedType
> = ({
  defaultNow,
  detailView,
  isFormLocked,
  detailViewTooltip,
  handleRefLinkClick,
  productView,
}) => {
  const { classes } = useStyles({ detailedView: detailView })
  const accountId = useAccountId({ product: 'flyout' }) || ''
  const { values, setFieldValue, errors, touched, handleBlur } =
    useFormikContext() as FormikProps<FlyoutProductSections>
  const { infoCollectionFormMetaData } = values
  const infoCollectionFormMetaDataErrors = errors.infoCollectionFormMetaData
  const infoCollectionFormMetaDataTouched = touched.infoCollectionFormMetaData

  const [bgColorType, setBgColorType] = useState<string>(
    infoCollectionFormMetaData?.bgColor
      ? infoCollectionFormMetaData?.bgColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )

  const [bgColor, setBgColor] = useState<string[]>(() => {
    const colorString = infoCollectionFormMetaData?.bgColor || ''
    return extractColorV2(bgColorType, colorString)
  })

  useEffect(() => {
    const colorString = infoCollectionFormMetaData?.bgColor || ''
    setBgColor(extractColorV2(bgColorType, colorString))
  }, [infoCollectionFormMetaData?.bgColor])

  const [buttonColorType, setButtonColorType] = useState<string>(
    infoCollectionFormMetaData?.buttonColor
      ? infoCollectionFormMetaData?.buttonColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )
  const [buttonColor, setButtonColor] = useState<string[]>(() => {
    const colorString = infoCollectionFormMetaData?.buttonColor || ''
    return extractColorV2(buttonColorType, colorString)
  })

  useEffect(() => {
    const colorString = infoCollectionFormMetaData?.buttonColor || ''
    setButtonColor(extractColorV2(buttonColorType, colorString))
  }, [infoCollectionFormMetaData?.buttonColor])

  const [buttonTextColorType, setButtonTextColorType] = useState<string>(
    infoCollectionFormMetaData?.buttonTextColor
      ? infoCollectionFormMetaData?.buttonTextColor?.includes('#')
        ? 'HEX'
        : 'RGB'
      : 'HEX'
  )
  const [buttonTextColor, setButtonTextColor] = useState<string[]>(() => {
    const colorString = infoCollectionFormMetaData?.buttonTextColor || ''
    return extractColorV2(buttonTextColorType, colorString)
  })

  useEffect(() => {
    const colorString = infoCollectionFormMetaData?.buttonTextColor || ''
    setButtonTextColor(extractColorV2(buttonTextColorType, colorString))
  }, [infoCollectionFormMetaData?.buttonTextColor])

  const handleTypeChange = async (
    event: SelectChangeEvent<unknown>,
    type: string
  ) => {
    const typeValue = event.target.value as string
    const resetValue = typeValue === 'RGB' ? 'rgba(0,0,0,100)' : ''
    switch (type) {
      case 'BG_COLOR':
        await setBgColorType(event.target.value as string)
        await setFieldValue('infoCollectionFormMetaData.bgColor', resetValue)
        break
      case 'BUTTON_COLOR':
        await setButtonColorType(event.target.value as string)
        await setFieldValue(
          'infoCollectionFormMetaData.buttonColor',
          resetValue
        )
        break
      case 'BUTTON_TEXT_COLOR':
        await setButtonTextColorType(event.target.value as string)
        await setFieldValue(
          'infoCollectionFormMetaData.buttonTextColor',
          resetValue
        )
        break
      default:
        break
    }
  }

  const handleColorChange = async (
    index: number,
    event: SelectChangeEvent,
    type: string
  ) => {
    switch (type) {
      case 'BG_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...bgColor],
          event.target.value as string
        )

        if (bgColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue('infoCollectionFormMetaData.bgColor', color)
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue('infoCollectionFormMetaData.bgColor', color)
        }
        return
      }
      case 'BUTTON_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...buttonColor],
          event.target.value as string
        )

        if (buttonColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue('infoCollectionFormMetaData.buttonColor', color)
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue('infoCollectionFormMetaData.buttonColor', color)
        }
        return
      }
      case 'BUTTON_TEXT_COLOR': {
        const updatedColorValues = calculateMinMaxResult(
          index,
          [...buttonTextColor],
          event.target.value as string
        )
        setButtonTextColor(updatedColorValues)
        if (buttonTextColorType === 'RGB') {
          const color = `rgba(${updatedColorValues[0]}, ${updatedColorValues[1]}, ${updatedColorValues[2]}, ${updatedColorValues[3]})`
          await setFieldValue(
            'infoCollectionFormMetaData.buttonTextColor',
            color
          )
        } else {
          const color = hexadecimalColor(
            `${updatedColorValues[4]}`,
            Number(updatedColorValues[3])
          )
          await setFieldValue(
            'infoCollectionFormMetaData.buttonTextColor',
            color
          )
        }
        return
      }
      default:
        break
    }
  }

  const updateBgImageUrl = (imageUrl: string) => {
    setFieldValue('infoCollectionFormMetaData.bgImageUrl', imageUrl)
  }
  const deleteBgImageUrl = () => {
    setFieldValue('infoCollectionFormMetaData.bgImageUrl', '')
  }

  const [indexModal, setIndexModal] = useState<number>(0)

  return (
    <BoxV2
      heading={'Buyer registration page branding'}
      subHeading={
        <>
          <div>
            <Typography className={classes.sectionSubHeading}>
              In this section, you have the option to customize the color scheme
              and background image of the buyer registration page to align with
              your company branding. The logo and company name previously
              provided by you will be incorporated into this buyer registration
              page.
            </Typography>
            <div className={classes.noteContainer}>
              <NoteV2 description="You may need assistance from your Marketing or Design team for details of this customization." />
            </div>
          </div>
        </>
      }
      headingIcon={
        <FontAwesomeIcon
          icon={faHashtag}
          style={{ width: '18px', height: '14px' }}
        />
      }
      secondaryActions={
        <FlyoutProductBrandingFaqs
          indexModal={indexModal}
          setIndexModal={setIndexModal}
        />
      }
    >
      <>
        <div className={classes.sectionContainer}>
          <div className={classes.textContainer}>
            <Typography
              className={classes.sectionHeading}
              data-testid="bg-color-text"
            >
              Background color
            </Typography>
            <Typography
              className={classes.sectionSubHeading}
              data-testid="bg-inner-color-text"
            >
              This color shows behind the white input box.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 2 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            followCursor={detailView}
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
          >
            <div className={classes.textWidth} data-testid="bg-color">
              <ColorSelectorV2
                data-testid={'bg-color-type-select'}
                selectedField={bgColorType}
                colorValues={bgColor}
                keyName={'infoCollectionFormMetaData.bgColor'}
                onChange={event => handleTypeChange(event, 'BG_COLOR')}
                handleColorChange={(index, event) =>
                  handleColorChange(index, event, 'BG_COLOR')
                }
                placeholder={'#FFFFFF'}
                disabled={isFormLocked || detailView}
                isError={
                  !!(
                    infoCollectionFormMetaDataErrors?.bgColor &&
                    infoCollectionFormMetaDataTouched?.bgColor
                  )
                }
                errorText={
                  (!!infoCollectionFormMetaDataTouched?.bgColor &&
                    infoCollectionFormMetaDataErrors?.bgColor) ||
                  ''
                }
                onBlur={handleBlur}
                color={convertRGBAAlpha(
                  infoCollectionFormMetaData.bgColor,
                  true
                )}
              />
            </div>
          </LabraToolTipV2>
        </div>

        <div
          className={
            productView ? classes.sectionContainer : classes.colorContainer
          }
        >
          <div className={classes.buttonColorInnerContainer}>
            <div className={classes.textContainer}>
              <Typography
                className={classes.sectionHeading}
                data-testid="button-color-text"
              >
                Button color
              </Typography>
              <Typography
                className={classes.sectionSubHeading}
                data-testid="button-inner-color-text"
              >
                Color of the 'Submit' button, visible when all mandatory fields
                are filled.{' '}
                <ButtonV2
                  styleType="link"
                  onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
                >
                  (Refer no. 4 in image no. 1)
                </ButtonV2>
              </Typography>
            </div>
            <LabraToolTipV2
              followCursor={detailView}
              tooltipPlacement="bottom-start"
              description={detailViewTooltip}
            >
              <div className={classes.textWidth} data-testid="button-color">
                <ColorSelectorV2
                  data-testid={'button-color-type-select'}
                  selectedField={buttonColorType}
                  keyName={'infoCollectionFormMetaData.buttonColor'}
                  colorValues={buttonColor}
                  onChange={event => handleTypeChange(event, 'BUTTON_COLOR')}
                  handleColorChange={(index, event) =>
                    handleColorChange(index, event, 'BUTTON_COLOR')
                  }
                  placeholder={'#FFFFFF'}
                  disabled={isFormLocked || detailView}
                  isError={
                    !!(
                      infoCollectionFormMetaDataErrors?.buttonColor &&
                      infoCollectionFormMetaDataTouched?.buttonColor
                    )
                  }
                  errorText={
                    (!!infoCollectionFormMetaDataTouched?.buttonColor &&
                      infoCollectionFormMetaDataErrors?.buttonColor) ||
                    ''
                  }
                  onBlur={handleBlur}
                  color={convertRGBAAlpha(
                    infoCollectionFormMetaData.buttonColor,
                    true
                  )}
                />
              </div>
            </LabraToolTipV2>
          </div>
          <Grid className={classes.colorInnerContainer}>
            <Grid className={classes.textContainer}>
              <Typography
                className={classes.sectionHeading}
                data-testid="button-text-color-text-heading"
              >
                Button text color
              </Typography>
              <Typography
                className={classes.sectionSubHeading}
                data-testid="button-inner-text-color-text-heading"
              >
                Color of the 'Submit' text in button, visible when all mandatory
                fields are filled.{' '}
                <ButtonV2
                  styleType="link"
                  onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
                >
                  (Refer no. 4 in image no. 1)
                </ButtonV2>
              </Typography>
            </Grid>
            <Grid>
              <LabraToolTipV2
                followCursor={detailView}
                tooltipPlacement="bottom-start"
                description={detailViewTooltip}
              >
                <div
                  className={classes.textWidth}
                  data-testid="button-text-color"
                >
                  <ColorSelectorV2
                    data-testid={'button-text-color-type-select'}
                    selectedField={buttonTextColorType}
                    keyName={'infoCollectionFormMetaData.buttonTextColor'}
                    colorValues={buttonTextColor}
                    onChange={event =>
                      handleTypeChange(event, 'BUTTON_TEXT_COLOR')
                    }
                    handleColorChange={(index, event) =>
                      handleColorChange(index, event, 'BUTTON_TEXT_COLOR')
                    }
                    placeholder={'#FFFFFF'}
                    disabled={isFormLocked || detailView}
                    isError={
                      !!(
                        infoCollectionFormMetaDataErrors?.buttonTextColor &&
                        infoCollectionFormMetaDataTouched?.buttonTextColor
                      )
                    }
                    errorText={
                      (!!infoCollectionFormMetaDataTouched?.buttonTextColor &&
                        infoCollectionFormMetaDataErrors?.buttonTextColor) ||
                      ''
                    }
                    onBlur={handleBlur}
                    color={convertRGBAAlpha(
                      infoCollectionFormMetaData.buttonTextColor,
                      true
                    )}
                  />
                </div>
              </LabraToolTipV2>
            </Grid>
          </Grid>
        </div>

        <div
          className={classes.sectionContainer}
          data-testid="file-uploader-bg-image"
        >
          <div className={classes.textContainer}>
            <Typography className={classes.sectionHeading}>
              Background image (Optional)
            </Typography>
            <Typography className={classes.sectionSubHeading}>
              This is the image you can add, which will appear in the background
              on the left side of the screen. We recommend adding a photo or
              image depicting your business or branding.{' '}
              <ButtonV2
                styleType="link"
                onClick={() => handleRefLinkClick && handleRefLinkClick(0)}
              >
                (Refer no. 5 in image no. 1)
              </ButtonV2>
            </Typography>
          </div>
          <LabraToolTipV2
            followCursor={detailView}
            tooltipPlacement="bottom-start"
            description={detailViewTooltip}
            className={classes.fullWidth}
          >
            <FileUploadWithLoader
              keyName={'background-image-branding'}
              partnerId={accountId}
              existingFileLinks={
                isEmpty(infoCollectionFormMetaData?.bgImageUrl)
                  ? []
                  : [infoCollectionFormMetaData?.bgImageUrl]
              }
              assetType="bg_image_url"
              folder="landing_page_bg_image_url"
              fileUploadDescription={imageFileUploadDescriptionLandingPage}
              quantity={1}
              fileUploadType="png/jpg"
              unit="MB"
              uploadFileSize={2}
              acceptedFormats={['.png', '.jpg']}
              updateUrl={updateBgImageUrl}
              deleteUrl={deleteBgImageUrl}
              defaultNow={defaultNow}
              fileUploadButtonText={fileUploadButtonText}
              fileFormatIncorrectText={fileFormatIncorrectText}
              fileUploadContainerTitle={fileUploadContainerTitle}
              disabled={isFormLocked || detailView}
              bucketName={bucketName}
              aspectRatios={['3:4']}
              maxPixels={1440}
              minPixels={1080}
              viewOnly={detailView}
              checkDuplicate
              showRealFileName
            />
          </LabraToolTipV2>
        </div>
      </>
    </BoxV2>
  )
}

export const calculateMinMaxResult = (
  index: number,
  updatedColorValues: string[],
  value: string
): string[] => {
  const newColorValues = [...updatedColorValues]
  if (index === 3) {
    newColorValues[index] =
      getValidValueBetweenMinMaxFloat(value, 0, 100)?.toString() ?? '0'
  } else if (index < 3) {
    newColorValues[index] =
      getValidValueBetweenMinMaxFloat(value, 0, 255)?.toString() ?? '0'
  } else {
    newColorValues[index] = value
  }
  return newColorValues
}
